// Copyright (c) Meta Platforms, Inc. and affiliates.
// All rights reserved.

import { RGBAColors } from "./Interfaces";

// This source code is licensed under the license found in the
// LICENSE file in the root directory of this source tree.

// Convert the onnx model mask prediction to ImageData
function arrayToImageData(input: any, width: number, height: number, color: RGBAColors) {
  const arr = new Uint8ClampedArray(4 * width * height).fill(90);
  for (let i = 0; i < input.length; i++) {

    // Threshold the onnx model mask prediction at 0.0
    // This is equivalent to thresholding the mask using predictor.model.mask_threshold
    // in python
    if (input[i] > 0.0) {
      arr[4 * i + 0] = color.r;
      arr[4 * i + 1] = color.g;
      arr[4 * i + 2] = color.b;
      arr[4 * i + 3] = color.a;
    }
  }
  return new ImageData(arr, height, width);
}

export function rgbaToRgb(imageData: ImageData) {
  const width = imageData.width;
  const height = imageData.height;
  const data = imageData.data;

  for (let i = 0; i < width * height; i += 4) {
    // Get the red, green, and blue values from the RGBA array.
    const red = data[i];
    const green = data[i + 1];
    const blue = data[i + 2];

    // Create a new array with the RGB values.
    const rgb = [red, green, blue];

    // Copy the RGB values to the ImageData object's data array.
    data[i] = rgb[0];
    data[i + 1] = rgb[1];
    data[i + 2] = rgb[2];
  }

  return imageData;
}

// Use a Canvas element to produce an image from ImageData
export function imageDataToImage(imageData: ImageData): HTMLImageElement {
  const canvas = imageDataToCanvas(imageData);
  const image = new Image();
  image.src = canvas.toDataURL();
  return image;
}

// Canvas elements can be created from ImageData
function imageDataToCanvas(imageData: ImageData) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  ctx?.putImageData(imageData, 0, 0);
  return canvas;
}

export function dataURItoBlob(dataURI: string) {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  var byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  // write the bytes of the string to an ArrayBuffer
  var ab = new ArrayBuffer(byteString.length);

  // create a view into the buffer
  var ia = new Uint8Array(ab);

  // set the bytes of the buffer to the correct values
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  var blob = new Blob([ab], { type: mimeString });
  return blob;

}

export async function toDataUrl(imageUrl?: string): Promise<any> {
  if (!imageUrl) return;
  return fetch(imageUrl, {
    headers: {
      'Cache-Control': 'no-cache'
    }
  })
    .then(response => response.blob())
    .then(blob => new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    }));
}

// Convert the onnx model mask output to an HTMLImageElement
export function onnxMaskToImage(input: any, width: number, height: number, color: RGBAColors) {
  return imageDataToImage(arrayToImageData(input, width, height, color));
}
